// src/pages/admin/RolesPermissions.tsx
import React, { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource, patchResource} from '../../../router/api';
import { useAuth } from '../../../hooks/useAuth'; // ✅ chemin corrigé

interface Permission {
  module: string;
  module_code: string;
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  validate: boolean;
}

interface Role {
  id: string;
  code: string;
  nom: string;
  description: string | null;
  user_count: number;
  permissions: Permission[];
}

export default function RolesPermissions() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const { user: currentUser } = useAuth();

  // Charger tous les rôles + permissions
  const loadRoles = async () => {
    try {
      setLoading(true);
      const res = await getResources(`roles/with-permissions`);
      setRoles(res.data.roles);
      if (res.data.roles.length > 0) {
        setSelectedRole(res.data.roles[0]);
      }
    } catch (err: any) {
      showNotification('error', err.response?.data?.message || 'Erreur lors du chargement des rôles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) loadRoles();
  }, [currentUser]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const handlePermissionChange = async (moduleCode: string, action: keyof Permission, value: boolean) => {
    if (!selectedRole || saving) return;

    setSaving(true);
    try {
      await patchResource(`roles/${selectedRole.id}/permissions`, {
        module_code: moduleCode,
        action,
        value
      });

      // Mise à jour locale optimiste
      setSelectedRole(prev => {
        if (!prev) return null;
        return {
          ...prev,
          permissions: prev.permissions.map(p =>
            p.module_code === moduleCode ? { ...p, [action]: value } : p
          )
        };
      });

      showNotification('success', 'Permission mise à jour');
    } catch (err: any) {
      showNotification('error', err.response?.data?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
    ) : (
      <i className="ri-close-circle-fill text-gray-300 text-2xl hover:text-gray-400 transition"></i>
    );
  };

  // ✅ Vérification des droits avec user.role.nom et est_superadmin
  if (!currentUser || (currentUser.role.nom !== 'Administrateur' && !currentUser.est_superadmin)) {
    return (
      <div className="p-10 text-center text-red-600 text-xl">
        <i className="ri-shield-cross-line text-6xl mb-4 block"></i>
        Accès refusé
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-screen-2xl mx-auto">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl text-white font-medium shadow-2xl transition-all ${
          notification.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {notification.message}
        </div>
      )}

      {saving && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Sauvegarde en cours...
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar - Liste des rôles */}
        <div className="lg:col-span-1 space-y-6">
          {/* Carte total */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-indigo-700 font-semibold">Rôles actifs</span>
              <i className="ri-shield-user-line text-3xl text-indigo-600 opacity-30"></i>
            </div>
            <p className="text-4xl font-bold text-indigo-900">{roles.length}</p>
          </div>

          {/* Liste des rôles */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-5 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <i className="ri-shield-keyhole-line"></i>
                Liste des Rôles
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-3xl"></i>
                </div>
              ) : (
                roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`w-full p-5 text-left transition-all hover:bg-gray-50 ${
                      selectedRole?.id === role.id
                        ? 'bg-blue-50 border-l-4 border-blue-600 shadow-sm'
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-900">{role.nom}</span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                        {role.user_count} {role.user_count > 1 ? 'utilisateurs' : 'utilisateur'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{role.description || 'Aucune description'}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Contenu principal - Permissions */}
        <div className="lg:col-span-3">
          {selectedRole ? (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Header du rôle */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold mb-2">{selectedRole.nom}</h2>
                    <p className="text-blue-100 text-lg max-w-2xl">
                      {selectedRole.description || 'Aucune description disponible'}
                    </p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3">
                    <p className="text-4xl font-bold">{selectedRole.user_count}</p>
                    <p className="text-sm opacity-90">utilisateur{selectedRole.user_count > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>

              {/* Tableau des permissions */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-settings-3-line text-blue-600"></i>
                  Permissions par Module
                </h3>

                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-8 py-5 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Module
                        </th>
                        {['read', 'create', 'update', 'delete', 'validate'].map(action => (
                          <th key={action} className="px-6 py-5 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">
                            {action === 'read' ? 'Lire' :
                             action === 'create' ? 'Créer' :
                             action === 'update' ? 'Modifier' :
                             action === 'delete' ? 'Supprimer' : 'Valider'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedRole.permissions.map((perm) => (
                        <tr key={perm.module_code} className="hover:bg-gray-50 transition">
                          <td className="px-8 py-6">
                            <span className="font-semibold text-gray-900 text-lg">{perm.module}</span>
                          </td>
                          {(['read', 'create', 'update', 'delete', 'validate'] as const).map(action => (
                            <td key={action} className="px-6 py-6 text-center">
                              <button
                                onClick={() => handlePermissionChange(perm.module_code, action, !perm[action])}
                                disabled={saving}
                                className="transition transform hover:scale-110 active:scale-95"
                                title={`${perm[action] ? 'Retirer' : 'Accorder'} le droit de ${action === 'read' ? 'lire' : action === 'create' ? 'créer' : action === 'update' ? 'modifier' : action === 'delete' ? 'supprimer' : 'valider'}`}
                              >
                                {getPermissionIcon(perm[action])}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Info box */}
                <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-4">
                    <i className="ri-information-line text-3xl text-amber-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-amber-900 mb-3 text-lg">Règles de Sécurité Appliquées</h4>
                      <ul className="space-y-2 text-amber-800">
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-blank-circle-fill text-xs"></i>
                          Les permissions sont appliquées en temps réel dans toute l’application
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-blank-circle-fill text-xs"></i>
                          Seuls les <strong>Administrateurs</strong> peuvent modifier les rôles
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-blank-circle-fill text-xs"></i>
                          Les Commerciaux n’ont jamais accès aux prix d’achat
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-blank-circle-fill text-xs"></i>
                          Les validations financières nécessitent le rôle <strong>Comptable</strong> ou supérieur
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl p-20 text-center">
              <i className="ri-shield-user-line text-8xl text-gray-300 mb-6 block"></i>
              <p className="text-xl text-gray-500">Sélectionnez un rôle pour voir ses permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
